import { ShapeAttrs } from '@antv/g-base';
import { colors } from 'ui/theme';
import { Group, Shape, ShapeElement } from 'ui/workflows/flowchart/types';
import { ShapeType } from '../../constants';
import { addLinkedShape, getLinkedShape } from '../helpers';
import { getHorizontalLinePath } from '../lineUtils';
import { GetAssociatedShapeName, getAssociatedShapeName } from './textHelpers';

const UNDERLINE_OFFSET = -5;

/** Gets the underline class name from associated text */
export const getTextUnderlineClassName = (args: GetAssociatedShapeName) =>
  getAssociatedShapeName({ ...args, suffix: 'underline' });

export const getTextUnderlineDimensions = (shape: Shape, { maxWidth = Infinity } = {}) => {
  const { width, height, x, y } = shape.getBBox();

  return { width: Math.min(maxWidth, width), height, x, y, lineY: y + height + 2, lineXMin: x + 1 };
};

/**
 * Gets the underline shape from the text shape
 */
export const getUnderline = (shape: Shape) => getLinkedShape(shape, ShapeType.Underline);

interface DrawTextUnderlineArgs extends GetAssociatedShapeName {
  /** The group the shape belongs to */
  group: Group;
  /** Additional styles to be added to the underline */
  style?: ShapeAttrs;
  /** Is the line dashed? */
  isDashed?: boolean;
  /** The max width of the line */
  maxWidth?: number;
}

/**
 * Adds a dashed underline to text
 */
export const drawTextUnderline = ({
  shape,
  group,
  className,
  isDashed = true,
  style = {},
  maxWidth = Infinity,
}: DrawTextUnderlineArgs) => {
  if (!shape || !group) {
    /* istanbul ignore next */
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        'drawTextUnderline: Please provide both the shape and the group when trying to underline text.',
      );
    }

    return;
  }

  const underlineClassName = getTextUnderlineClassName({ shape, className });
  const { width, x, y, lineY, lineXMin } = getTextUnderlineDimensions(shape, { maxWidth });
  const attrs = {
    x,
    y: y + UNDERLINE_OFFSET,
    path: getHorizontalLinePath({ x: lineXMin, y: lineY, width }),
    stroke: (shape as ShapeElement)?.attrs?.fill || colors.black,
    cursor: 'pointer',
    shapeType: ShapeType.Underline,
    ...(isDashed ? { lineDash: [2] } : {}),
    ...style,
  };
  const underline = group.addShape('path', {
    name: underlineClassName,
    className: underlineClassName,
    attrs,
  });

  addLinkedShape(shape, ShapeType.Underline, underline);

  return underline;
};

/**
 * Updates Underline
 */
export const updateUnderline = ({ shape }: { shape: Shape }) => {
  const { width, lineY, lineXMin } = getTextUnderlineDimensions(shape);
  const underlineShape = getUnderline(shape);

  underlineShape?.attr('path', getHorizontalLinePath({ x: lineXMin, y: lineY, width }));
};

/**
 * Updates text that has an underline
 */
export const updateTextWithUnderline = ({ shape, text }: { shape: Shape; text?: string }) => {
  if (text) {
    shape.attr('text', text);
  }
  updateUnderline({ shape });
};
